import { useLocation, useNavigate } from 'react-router-dom';
import {
  FiHome,
  FiCalendar,
  FiScissors,
  FiShoppingCart,
  FiUsers,
  FiUserCheck,
  FiPackage,
  FiGift,
  FiSend,
  FiBarChart2,
  FiSettings,
  FiShield,
  FiPercent,
  FiX,
  FiFileText,
  FiStar,
  FiBookOpen,
  FiDroplet,
  FiLogOut,
  FiActivity,
  FiShare2,
  FiGrid,
  FiZap,
  FiPieChart,
  FiList,
  FiClipboard,
} from 'react-icons/fi';
import { FaRupeeSign } from 'react-icons/fa';
import { useAuth } from '../../contexts/AuthContext';
import { hasAccess } from '../../utils/permissions';

const navSections = [
  {
    label: 'MAIN',
    items: [
      { id: 'billing', label: 'Billing', icon: FiShoppingCart, path: '/' },
      { id: 'appointments', label: 'Appointments', icon: FiCalendar, path: '/appointments' },
      { id: 'services', label: 'Services', icon: FiScissors, path: '/services' },
      { id: 'sop', label: 'Service SOP', icon: FiList, path: '/sop' },
      { id: 'beauty-profile', label: 'Beauty Profile', icon: FiClipboard, path: '/beauty-profile' },
      { id: 'transactions', label: 'Transactions', icon: FiFileText, path: '/transactions' },
      { id: 'my-performance', label: 'Staff Performance', icon: FiActivity, path: '/my-performance' },
    ],
  },
  {
    label: 'MANAGEMENT',
    items: [
      { id: 'customers', label: 'Customers', icon: FiUsers, path: '/customers' },
      { id: 'employees', label: 'Staff', icon: FiUserCheck, path: '/employees' },
      { id: 'staff-roster', label: 'Staff Roster', icon: FiGrid, path: '/staff-roster' },
      { id: 'payroll', label: 'Payroll', icon: FaRupeeSign, path: '/payroll' },
      { id: 'inventory', label: 'Products', icon: FiPackage, path: '/inventory' },
      { id: 'product-usage', label: 'Product Usage', icon: FiDroplet, path: '/product-usage' },
      { id: 'accounts', label: 'Accounts', icon: FiBookOpen, path: '/accounts' },
      { id: 'ledger', label: 'Ledger', icon: FiFileText, path: '/ledger' },
      { id: 'stylist-directory', label: 'Stylist Directory', icon: FiUsers, path: '/stylist-directory' },
    ],
  },
  {
    label: 'GROWTH',
    items: [
      { id: 'referrals', label: 'Referrals', icon: FiShare2, path: '/referrals' },
      { id: 'packages', label: 'Packages', icon: FiGift, path: '/packages' },
      { id: 'offers', label: 'Offers', icon: FiPercent, path: '/offers' },
      { id: 'marketing', label: 'Marketing', icon: FiSend, path: '/marketing' },
    ],
  },
  {
    label: 'ANALYTICS',
    items: [
      { id: 'reports', label: 'Reports', icon: FiBarChart2, path: '/reports' },
      { id: 'customer-insights', label: 'Customer Insights', icon: FiPieChart, path: '/customer-insights' },
      { id: 'service-insights', label: 'Service Insights', icon: FiScissors, path: '/service-insights' },
      { id: 'staff-insights', label: 'Staff Insights', icon: FiUserCheck, path: '/staff-insights' },
      { id: 'product-insights', label: 'Product Insights', icon: FiPackage, path: '/product-insights' },
    ],
  },
  {
    label: 'SYSTEM',
    items: [
      { id: 'users', label: 'User Management', icon: FiShield, path: '/users' },
      { id: 'settings', label: 'Settings', icon: FiSettings, path: '/settings' },
    ],
  },
];

const Sidebar = ({ open, onClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const handleNav = (path) => {
    navigate(path);
    if (window.innerWidth < 1024) {
      onClose();
    }
  };

  return (
    <>
      {/* Mobile backdrop */}
      {open && (
        <div
          className="fixed inset-0 bg-slate-900/20 backdrop-blur-sm z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed inset-y-0 left-0 z-50 w-[21.6667rem] bg-slate-900
          border-r border-slate-800
          flex flex-col transform transition-transform duration-300 ease-salon
          lg:static lg:translate-x-0
          ${open ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        {/* Brand */}
        <div className="flex items-center justify-between px-6 py-5">
          <div className="flex items-center gap-3">
            <img
              src="/logo.png"
              alt="Gumma's Glam House"
              className="w-10 h-10 rounded-full object-cover ring-2 ring-gold-500/40 shadow-salon-gold"
            />
            <div className="leading-tight">
              <span className="font-display text-[1.15rem] font-semibold text-white block tracking-tight">Gumma's</span>
              <span className="text-[0.8333rem] tracking-[0.25em] text-gold-400 uppercase font-semibold">Glam House</span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-800 hover:text-slate-300 lg:hidden transition-colors"
          >
            <FiX size={18} />
          </button>
        </div>

        {/* Divider */}
        <div className="mx-5 h-px bg-slate-800" />

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-5">
          {navSections.map((section) => {
            const visibleItems = section.items.filter((item) =>
              hasAccess(user?.role, item.id)
            );
            if (visibleItems.length === 0) return null;

            return (
              <div key={section.label}>
                <p className="px-3 mb-2.5 text-[0.8333rem] font-bold tracking-[0.2em] text-slate-500 uppercase">
                  {section.label}
                </p>
                <div className="space-y-0.5">
                  {visibleItems.map((item) => {
                    const Icon = item.icon;
                    const active = isActive(item.path);
                    return (
                      <button
                        key={item.id}
                        onClick={() => handleNav(item.path)}
                        className={`
                          w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-[1.0833rem]
                          transition-all duration-200 ease-salon relative group
                          ${
                            active
                              ? 'bg-gold-500/15 text-gold-400 font-semibold'
                              : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'
                          }
                        `}
                      >
                        {active && (
                          <span className="absolute left-0 top-1/2 -translate-y-1/2 w-[0.25rem] h-5 rounded-r-full bg-gold-500" />
                        )}
                        <Icon
                          size={18}
                          className={`transition-colors duration-200 ${active ? 'text-gold-400' : 'text-slate-500 group-hover:text-slate-300'}`}
                        />
                        <span className="tracking-wide">{item.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </nav>

        {/* Bottom: user info + logout */}
        <div className="mx-5 h-px bg-slate-800" />
        <div className="px-3 py-4 flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-gradient-gold flex items-center justify-center text-white text-xs font-semibold shrink-0">
            {user?.name ? user.name.charAt(0).toUpperCase() : 'U'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[1.0833rem] font-semibold text-slate-200 truncate">{user?.name || 'User'}</p>
            <p className="text-[0.8333rem] text-slate-500 truncate capitalize">{user?.role || ''}</p>
          </div>
          <button
            onClick={logout}
            title="Logout"
            className="p-2 rounded-lg text-slate-500 hover:bg-rose-500/10 hover:text-rose-400 transition-colors shrink-0"
          >
            <FiLogOut size={16} />
          </button>
        </div>

      </aside>
    </>
  );
};

export default Sidebar;
